/**
 * The pitch engine: one probe bank, six answers.
 *
 * This replaces the transform-and-search design. That one asked "what pitches
 * are in this window", which needs 683 ms of signal to resolve a semitone at
 * the low E, and then had to guess which measurement belonged to which string.
 * This one asks the question the tuner actually has: "how far is each of these
 * six known strings from where it should be" — and answers it in about a tenth
 * of the time, because a probe only has to reject what is nearby rather than
 * resolve everything.
 *
 * The extra partials are not waste. They are what tells a string apart from a
 * lower string's overtone; see `harmonic-model.ts`.
 */

import { Probe } from './probe'
import { captureRangeCents, planProbes, type ProbePlan, type ProbePlanOptions } from './probe-plan'
import { COINCIDENT_CENTS } from './probe-plan'
import { judgeGhost, type GhostQuery, type PartialAmplitude } from '../music/harmonic-model'
import { CENTS_PER_OCTAVE } from '../music/pitch'

/** What the tracker reports for one string. */
export interface StringReading {
  /** Deviation from the target in cents, or null when nothing is heard. */
  readonly cents: number | null
  /** Measured frequency in Hz, or null. */
  readonly frequency: number | null
  /** Amplitude at the string's fundamental. */
  readonly amplitude: number
  /**
   * True when a lower string's overtone accounts for the whole reading.
   *
   * Reported rather than hidden: the caller decides whether a ghost is shown
   * dimly or not at all.
   */
  readonly isGhost: boolean
}

/** Tracker configuration. */
export interface TrackerOptions extends ProbePlanOptions {
  /**
   * Amplitude below which a string counts as silent.
   *
   * A probe always reports something — noise mixed to baseband is still a
   * number. This is the line under which that number means nothing.
   */
  readonly silenceAmplitude?: number
  /**
   * Share of the loudest string below which one counts as silent.
   *
   * Absolute floors alone are not enough. A four-pole filter rejects a
   * neighbour 36 Hz away by about 60 dB, which sounds like plenty until the
   * neighbour is a loud open string — then what leaks through still clears
   * any floor set low enough for a quiet room, and the probe locks onto it
   * and reports a confident number for a string nobody touched.
   *
   * Strings within one strum stay within about 20 dB of each other, so 34 dB
   * down is far below anything real and far above anything leaked.
   */
  readonly silenceRelative?: number
}

/** Default noise floor, measured against a quiet interface input. */
export const DEFAULT_SILENCE_AMPLITUDE = 2e-4

/** Default relative floor: 34 dB below the loudest string. */
export const DEFAULT_SILENCE_RELATIVE = 0.02

/**
 * Agreeing readings before the ghost verdict is allowed to flip.
 *
 * The amplitude ratio wobbles around the threshold for a string only just
 * louder than its explanation, and a bar that blinks in and out is worse than
 * one that is steadily wrong. At one block every 10 ms this costs a third of
 * the time a needle takes to move anyway.
 */
export const GHOST_VOTES = 4

const centsBetween = (a: number, b: number): number => Math.abs(CENTS_PER_OCTAVE * Math.log2(a / b))

/** Which lower string, if any, could explain each string's fundamental. */
interface Explanation {
  readonly lowerString: number
  readonly partial: number
}

/** Tracks a fixed set of string targets through a stream of samples. */
export class StringTracker {
  private readonly probes: Probe[]
  private readonly explanations: Explanation[][]
  private readonly silenceAmplitude: number
  private readonly silenceRelative: number
  /** Consecutive ghost votes per string; see {@link GHOST_VOTES}. */
  private readonly ghostVotes: Int32Array
  /** How far a string may stray before its probe stops believing itself. */
  private readonly captureRange: readonly number[]

  constructor(
    readonly plan: ProbePlan,
    options: TrackerOptions = {},
  ) {
    this.silenceAmplitude = options.silenceAmplitude ?? DEFAULT_SILENCE_AMPLITUDE
    this.silenceRelative = options.silenceRelative ?? DEFAULT_SILENCE_RELATIVE
    this.ghostVotes = new Int32Array(plan.targets.length)
    this.captureRange = plan.targets.map((_, index) => captureRangeCents(plan, index))
    this.probes = plan.probes.map(
      (planned) =>
        new Probe({
          frequency: planned.frequency,
          sampleRate: plan.sampleRate,
          bandwidthHz: planned.bandwidthHz,
          windowSamples: planned.windowSamples,
        }),
    )

    // Which lower strings land on which — computed once, not per frame.
    this.explanations = plan.targets.map((target, index) => {
      const found: Explanation[] = []
      for (let lower = 0; lower < index; lower += 1) {
        const lowerTarget = plan.targets[lower]!
        if (lowerTarget >= target) continue
        const ratio = target / lowerTarget
        const partial = Math.round(ratio)
        if (partial < 2) continue
        if (centsBetween(target, lowerTarget * partial) > COINCIDENT_CENTS) continue
        found.push({ lowerString: lower, partial })
      }
      return found
    })
  }

  /** Number of strings tracked. */
  get size(): number {
    return this.plan.targets.length
  }

  /** Feed a block of samples at the plan's sample rate. */
  push(samples: Float32Array): void {
    for (const probe of this.probes) probe.pushBlock(samples)
  }

  /** Forget everything, e.g. after the input was switched. */
  reset(): void {
    for (const probe of this.probes) probe.reset()
    this.ghostVotes.fill(0)
  }

  /**
   * Read all strings.
   *
   * Not a pure query: each call advances the ghost vote, so call it once per
   * block of samples rather than opportunistically.
   */
  read(): readonly StringReading[] {
    const amplitudes = this.probes.map((probe) => probe.read())

    // The loudest fundamental sets the floor the others are judged against.
    let loudest = 0
    this.plan.probes.forEach((planned, i) => {
      if (planned.partial === 1) loudest = Math.max(loudest, amplitudes[i]!.amplitude)
    })
    const floor = Math.max(this.silenceAmplitude, loudest * this.silenceRelative)

    /** Measured partials of one string, for the harmonic fit. */
    const partialsOf = (stringIndex: number): PartialAmplitude[] =>
      this.plan.probes
        .map((planned, i) => ({ planned, reading: amplitudes[i]! }))
        .filter((entry) => entry.planned.stringIndex === stringIndex)
        .map((entry) => ({
          partial: entry.planned.partial,
          amplitude: entry.reading.amplitude,
          isFree: entry.planned.isFree,
        }))

    return this.plan.targets.map((target, stringIndex) => {
      const index = this.plan.probes.findIndex(
        (planned) => planned.stringIndex === stringIndex && planned.partial === 1,
      )
      const reading = index >= 0 ? amplitudes[index]! : { cents: null, amplitude: 0 }

      // Outside its own passband a probe is not measuring its string, it is
      // measuring whatever leaked in from a loud neighbour — and reporting
      // that as a deviation would put a confident wrong number on the meter.
      const outOfRange =
        reading.cents !== null && Math.abs(reading.cents) > this.captureRange[stringIndex]!

      if (reading.cents === null || outOfRange || reading.amplitude < floor) {
        this.ghostVotes[stringIndex] = 0
        return { cents: null, frequency: null, amplitude: reading.amplitude, isGhost: false }
      }

      const query: GhostQuery = {
        amplitude: reading.amplitude,
        explanations: this.explanations[stringIndex]!.map((explanation) => ({
          partial: explanation.partial,
          partials: partialsOf(explanation.lowerString),
        })),
      }
      const verdict = judgeGhost(query)

      const votes = Math.max(
        0,
        Math.min(GHOST_VOTES, this.ghostVotes[stringIndex]! + (verdict.isGhost ? 1 : -1)),
      )
      this.ghostVotes[stringIndex] = votes

      return {
        cents: reading.cents,
        frequency: target * Math.pow(2, reading.cents / CENTS_PER_OCTAVE),
        amplitude: reading.amplitude,
        isGhost: votes >= GHOST_VOTES,
      }
    })
  }
}

/** Build a tracker for a set of string targets. */
export function createTracker(
  targets: readonly number[],
  sampleRate: number,
  options: TrackerOptions = {},
): StringTracker {
  return new StringTracker(planProbes(targets, sampleRate, options), options)
}
